import { API_BASE_URL, WS_BASE_URL } from "../../config";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Button from '../Components/Button';

const HardWare = () => {
    const [activeTab, setActiveTab] = useState('Devices');

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', borderBottom: '2px solid #ddd', marginBottom: '20px' }}>
                {['Devices', 'Tokens'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '10px 20px',
                            cursor: 'pointer',
                            backgroundColor: activeTab === tab ? '#007bff' : '#f8f9fa',
                            color: activeTab === tab ? 'white' : 'black',
                            border: 'none',
                            borderBottom: activeTab === tab ? '2px solid #0056b3' : 'none',
                            marginRight: '5px',
                            borderRadius: '5px 5px 0 0',
                            fontWeight: 'bold'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            <div>
                {activeTab === 'Devices' && <HardwareV4 />}
                {activeTab === 'Tokens' && <DeviceTokens />}
            </div>
        </div>
    );
};

// --- V4 Component (device list + detail + Command/Config/Debug actions; V4 backend still has no
// update-details endpoint, so unlike the old V3 tab there's no Edit here) ---
const HardwareV4 = () => {
    const [devices, setDevices] = useState([]);
    const [tagGroupByHwid, setTagGroupByHwid] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [detailDevice, setDetailDevice] = useState(null);
    const [commandModalDevice, setCommandModalDevice] = useState(null);
    const [configModalDevice, setConfigModalDevice] = useState(null);
    const [relayAutomationDevice, setRelayAutomationDevice] = useState(null);
    const [debugDevice, setDebugDevice] = useState(null);
    const anyCardOpenRef = useRef(false);
    anyCardOpenRef.current = !!(commandModalDevice || configModalDevice || relayAutomationDevice || detailDevice || debugDevice);

    const API_BASE = `${API_BASE_URL}/hwcontrol/api/v4/hardware`;
    const TOKEN_API = `${API_BASE_URL}/hwcontrol/api/v3/hardware/tokens`;

    const fetchDevices = async () => {
        setLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;

            const headers = {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
            };
            const resp = await fetch(API_BASE, {
                method: 'GET',
                headers
            });
            const tokenResp = await fetch(TOKEN_API, {
                method: 'GET',
                headers
            }).catch(() => null);
            if (resp.ok) {
                const data = await resp.json();
                setDevices(data);
                if (tokenResp?.ok) {
                    const tokens = await tokenResp.json();
                    const latestGroupByHwid = {};
                    [...tokens]
                        .filter(tokenItem => !tokenItem.revoked && tokenItem.tagGroupName)
                        .sort((a, b) => b.id - a.id)
                        .forEach(tokenItem => {
                            if (!latestGroupByHwid[tokenItem.hwid]) {
                                latestGroupByHwid[tokenItem.hwid] = tokenItem.tagGroupName;
                            }
                        });
                    setTagGroupByHwid(latestGroupByHwid);
                } else {
                    setTagGroupByHwid({});
                }
                setError(null);
            } else {
                setError(`Failed to fetch hardware data: ${resp.status}`);
            }
        } catch (err) {
            setError(`Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSendCommand = async (hwid, commandRef, command) => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;

            const resp = await fetch(`${API_BASE}/${hwid}/command`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({ commandRef, command })
            });
            if (resp.ok) {
                alert("Command queued successfully");
                setCommandModalDevice(null);
                fetchDevices();
            } else {
                alert(`Failed to send command: ${resp.status}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleUpdateConfig = async (hwid, hwMode, config) => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;

            const resp = await fetch(`${API_BASE}/${hwid}/config`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({ hwMode, config })
            });
            if (resp.ok) {
                alert("Configuration updated successfully");
                setConfigModalDevice(null);
                setRelayAutomationDevice(null);
                fetchDevices();
            } else {
                alert(`Failed to update config: ${resp.status}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    useEffect(() => {
        fetchDevices();
        const interval = setInterval(() => {
            if (!anyCardOpenRef.current) fetchDevices();
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const groupedDevices = useMemo(() => {
        const groups = devices.reduce((result, device) => {
            const groupName = tagGroupByHwid[device.hwid] || 'ไม่จัดกลุ่ม';
            if (!result[groupName]) result[groupName] = [];
            result[groupName].push(device);
            return result;
        }, {});

        return Object.entries(groups).sort(([left], [right]) => {
            if (left === 'ไม่จัดกลุ่ม') return 1;
            if (right === 'ไม่จัดกลุ่ม') return -1;
            return left.localeCompare(right, 'th');
        });
    }, [devices, tagGroupByHwid]);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <h3>Hardware Devices (V4) — จัดตาม Tag Group</h3>
                <button onClick={fetchDevices} disabled={loading} style={{ padding: '5px 10px', cursor: 'pointer' }}>Refresh</button>
            </div>
            {loading && <div>Loading...</div>}
            {error && <div style={{ color: 'red' }}>{error}</div>}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ backgroundColor: '#f4f4f4', textAlign: 'left' }}>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>HWID</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Tag Group</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Name</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Description</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Type</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Status</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Mode</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Config Version</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Last Seen</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {groupedDevices.length === 0 && !loading && (
                        <tr><td colSpan="10" style={{ padding: '10px', border: '1px solid #ddd' }}>ไม่พบ Hardware</td></tr>
                    )}
                    {groupedDevices.map(([groupName, groupDevices]) => (
                        <React.Fragment key={groupName}>
                            <tr style={{ backgroundColor: '#e9f2ff' }}>
                                <td colSpan="10" style={{ padding: '10px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                                    Tag Group: {groupName} ({groupDevices.length})
                                </td>
                            </tr>
                            {groupDevices.map(device => (
                        <tr key={device.hwid}>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{device.hwid}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{tagGroupByHwid[device.hwid] || 'ไม่จัดกลุ่ม'}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{device.name || "-"}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{device.description || "-"}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{device.type}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{device.status}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{device.hwMode === 1 ? 'Normal' : 'Streaming'}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{device.configVersion}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{device.lastSeen ? new Date(device.lastSeen).toLocaleString() : '-'}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                    <Button variant="secondary" size="sm" onClick={() => setDetailDevice(device)}>
                                        Detail
                                    </Button>
                                    <Button variant="warning" size="sm" onClick={() => setCommandModalDevice(device)}>
                                        Command
                                    </Button>
                                    <Button variant="purple" size="sm" onClick={() => setConfigModalDevice(device)}>
                                        Config
                                    </Button>
                                    {device.capability?.deviceKind === 'relay' && (
                                        <Button variant="info" size="sm" onClick={() => setRelayAutomationDevice(device)}>
                                            Relay
                                        </Button>
                                    )}
                                    <Button variant="secondary" size="sm" onClick={() => setDebugDevice(device)}>
                                        Debug
                                    </Button>
                                </div>
                            </td>
                        </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            {commandModalDevice && (
                <SendCommandModal
                    device={commandModalDevice}
                    onSend={(ref, cmd) => handleSendCommand(commandModalDevice.hwid, ref, cmd)}
                    onClose={() => setCommandModalDevice(null)}
                />
            )}
            {configModalDevice && (
                <UpdateConfigModal
                    device={configModalDevice}
                    onSave={(mode, cfg) => handleUpdateConfig(configModalDevice.hwid, mode, cfg)}
                    onClose={() => setConfigModalDevice(null)}
                />
            )}
            {relayAutomationDevice && (
                <RelayAutomationModal
                    device={relayAutomationDevice}
                    devices={devices}
                    onSave={(config) => handleUpdateConfig(relayAutomationDevice.hwid, relayAutomationDevice.hwMode || 1, config)}
                    onClose={() => setRelayAutomationDevice(null)}
                />
            )}
            {detailDevice && (
                <DeviceDetailCard
                    device={detailDevice}
                    onClose={() => setDetailDevice(null)}
                />
            )}
            {debugDevice && (
                <SerialDebugCard
                    device={debugDevice}
                    onClose={() => setDebugDevice(null)}
                />
            )}
        </div>
    );
};

const SendCommandModal = ({ device, onSend, onClose }) => {
    const [commandRef, setCommandRef] = useState("");
    const [commandJson, setCommandJson] = useState('{\n  "action": "restart"\n}');

    const handleSubmit = () => {
        try {
            const parsed = JSON.parse(commandJson);
            onSend(commandRef, parsed);
        } catch (e) {
            alert("Invalid JSON: " + e.message);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', maxWidth: '600px', width: '90%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: '15px' }}>
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Command Reference (Optional):</label>
                        <input
                            type="text"
                            value={commandRef}
                            onChange={(e) => setCommandRef(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                    </div>
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Command (JSON):</label>
                        <textarea
                            value={commandJson}
                            onChange={(e) => setCommandJson(e.target.value)}
                            rows="5"
                            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'monospace' }}
                        />
                    </div>
                </div>
                <div style={{ padding: '10px 15px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button variant="warning" onClick={handleSubmit}>Send Command</Button>
                </div>
            </div>
        </div>
    );
};

const UpdateConfigModal = ({ device, onSave, onClose }) => {
    const [hwMode, setHwMode] = useState(device.hwMode || 1);
    const [configJson, setConfigJson] = useState(JSON.stringify(device.config || {}, null, 2));

    const handleSubmit = () => {
        try {
            const parsed = JSON.parse(configJson);
            onSave(hwMode, parsed);
        } catch (e) {
            alert("Invalid JSON: " + e.message);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', maxWidth: '600px', width: '90%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: '15px' }}>
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Hardware Mode:</label>
                        <select
                            value={hwMode}
                            onChange={(e) => setHwMode(Number(e.target.value))}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                        >
                            <option value={1}>Normal (1)</option>
                            <option value={2}>Streaming (2)</option>
                            <option value={3}>Maintenance (3)</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Config (JSON):</label>
                        <textarea
                            value={configJson}
                            onChange={(e) => setConfigJson(e.target.value)}
                            rows="8"
                            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'monospace' }}
                        />
                    </div>
                </div>
                <div style={{ padding: '10px 15px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button variant="purple" onClick={handleSubmit}>Update Config</Button>
                </div>
            </div>
        </div>
    );
};

// Relay automation lives with the relay device, not in a separate screen.  The
// backend owns evaluation: source telemetry -> versioned setRelay command.
const RelayAutomationModal = ({ device, devices, onSave, onClose }) => {
    const initialConfig = device.config || {};
    const channelCount = Number(device.capability?.channelCount) || 16;
    const [rules, setRules] = useState(Array.isArray(initialConfig.relayRules) ? initialConfig.relayRules : []);
    const [controlMode, setControlMode] = useState(initialConfig.controlMode || 'server');
    const sources = devices.filter(item => item.hwid !== device.hwid);

    const updateRule = (index, field, value) => {
        setRules(previous => previous.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, [field]: value } : rule));
    };
    const addRule = () => {
        setRules(previous => [...previous, {
            sourceHwid: sources[0]?.hwid || '', key: 'sonarDistanceCm', channel: 1, on: 100, off: 80
        }]);
    };
    const save = () => {
        const normalized = rules.map(rule => ({
            sourceHwid: String(rule.sourceHwid || '').trim(),
            key: String(rule.key || '').trim(),
            channel: Number(rule.channel), on: Number(rule.on), off: Number(rule.off)
        }));
        if (controlMode === 'server' && normalized.some(rule => !rule.sourceHwid || !rule.key || !Number.isInteger(rule.channel)
            || rule.channel < 1 || rule.channel > channelCount || !Number.isFinite(rule.on) || !Number.isFinite(rule.off))) {
            alert(`กรอกกฎให้ครบ: channel ต้องอยู่ระหว่าง 1-${channelCount}`);
            return;
        }
        onSave({ ...initialConfig, controlMode, relayRules: normalized });
    };

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', width: '94%', maxWidth: '1050px', maxHeight: '88vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={event => event.stopPropagation()}>
                <div style={{ padding: '18px', borderBottom: '1px solid #eee' }}>
                    <h3 style={{ margin: '0 0 6px' }}>Relay automation — {device.hwid}</h3>
                    <div style={{ color: '#666', fontSize: '13px' }}>Sensor ส่ง telemetry เข้า server แล้ว server จะส่งคำสั่ง <code>setRelay</code> กลับมายัง Relay</div>
                </div>
                <div style={{ padding: '18px' }}>
                    <label style={{ display: 'block', marginBottom: '14px' }}>
                        <span style={{ fontWeight: 'bold', marginRight: '10px' }}>Control mode</span>
                        <select value={controlMode} onChange={event => setControlMode(event.target.value)} style={{ padding: '7px' }}>
                            <option value="server">Server automation</option>
                            <option value="manual">Manual only</option>
                        </select>
                    </label>
                    {controlMode === 'server' && <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                                <thead><tr style={{ background: '#f4f7fb', textAlign: 'left' }}>
                                    {['Source device', 'Telemetry key', 'Relay', 'ON when value ≥', 'OFF when value ≤', ''].map(label => <th key={label} style={{ padding: '9px', borderBottom: '1px solid #ddd' }}>{label}</th>)}
                                </tr></thead>
                                <tbody>{rules.map((rule, index) => (
                                    <tr key={index}>
                                        <td style={{ padding: '7px' }}><select value={rule.sourceHwid || ''} onChange={event => updateRule(index, 'sourceHwid', event.target.value)} style={{ width: '190px', padding: '6px' }}>
                                            <option value="">เลือก sensor…</option>{sources.map(source => <option value={source.hwid} key={source.hwid}>{source.name || source.hwid} ({source.hwid})</option>)}</select></td>
                                        <td style={{ padding: '7px' }}><input value={rule.key || ''} onChange={event => updateRule(index, 'key', event.target.value)} placeholder="sonarDistanceCm" style={{ padding: '6px', width: '145px' }} /></td>
                                        <td style={{ padding: '7px' }}><select value={rule.channel ?? 1} onChange={event => updateRule(index, 'channel', event.target.value)} style={{ padding: '6px' }}>{Array.from({ length: channelCount }, (_, item) => <option key={item + 1} value={item + 1}>Relay {item + 1}</option>)}</select></td>
                                        <td style={{ padding: '7px' }}><input type="number" value={rule.on ?? ''} onChange={event => updateRule(index, 'on', event.target.value)} style={{ padding: '6px', width: '100px' }} /></td>
                                        <td style={{ padding: '7px' }}><input type="number" value={rule.off ?? ''} onChange={event => updateRule(index, 'off', event.target.value)} style={{ padding: '6px', width: '100px' }} /></td>
                                        <td style={{ padding: '7px' }}><Button variant="danger" size="sm" onClick={() => setRules(previous => previous.filter((_, ruleIndex) => ruleIndex !== index))}>ลบ</Button></td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                        {!sources.length && <div style={{ marginTop: '10px', color: '#a15c00', fontSize: '13px' }}>ยังไม่มี source device อื่นในรายการ — เพิ่มได้หลัง sensor register แล้ว</div>}
                        <button onClick={addRule} style={{ marginTop: '14px', padding: '7px 12px', border: '1px solid #0d6efd', borderRadius: '4px', color: '#0d6efd', background: 'white', cursor: 'pointer' }}>+ เพิ่มกฎ</button>
                    </>}
                </div>
                <div style={{ padding: '12px 18px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="info" onClick={save}>Save automation</Button>
                </div>
            </div>
        </div>
    );
};

const DeviceDetailCard = ({ device, onClose }) => {
    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', maxWidth: '600px', width: '90%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <strong>{device.hwid} — Detail</strong>
                    <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.9em' }}>
                    <div><strong>Capability:</strong> <pre style={{ maxHeight: '200px', overflow: 'auto' }}>{JSON.stringify(device.capability, null, 2)}</pre></div>
                    <div><strong>Config:</strong> <pre style={{ maxHeight: '200px', overflow: 'auto' }}>{JSON.stringify(device.config, null, 2)}</pre></div>
                    <div><strong>Last Data:</strong> <pre style={{ maxHeight: '200px', overflow: 'auto' }}>{JSON.stringify(device.lastData, null, 2)}</pre></div>
                    <div><strong>Last Command Result:</strong> <pre style={{ maxHeight: '200px', overflow: 'auto' }}>{JSON.stringify(device.lastCommandResult, null, 2)}</pre></div>
                </div>
            </div>
        </div>
    );
};

const SerialDebugCard = ({ device, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [autoScroll, setAutoScroll] = useState(true);
    const API_BASE = `${API_BASE_URL}/hwcontrol/api/v4/hardware`;

    const setDeviceHwMode = async (hwid, hwMode) => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;
            await fetch(`${API_BASE}/${hwid}/config`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({ hwMode })
            });
        } catch (err) {
            // best-effort mode flip; the log stream itself doesn't depend on this succeeding
        }
    };

    useEffect(() => {
        setDeviceHwMode(device.hwid, 2);
        // Consumer auth: pass the operator JWT as a query param (WS can't set Authorization header).
        const wsToken = JSON.parse(localStorage.getItem("user"))?.token;
        const socket = new WebSocket(`${WS_BASE_URL}/hwcontrol/api/ws/serial?hwid=${device.hwid}&type=${device.type}&access_token=${encodeURIComponent(wsToken || "")}`);
        socket.onopen = () => {
            setMessages(prev => [...prev, `Connected to WebSocket for ${device.hwid}`]);
        };
        socket.onmessage = (event) => {
            const rawMessage = event.data;
            const match = rawMessage.match(/^\[(.*?):(.*?)\]\s(.*)/);
            let parsedMessage;
            if (match) {
                parsedMessage = {
                    type: match[1],
                    hwid: match[2],
                    data: match[3],
                    timestamp: new Date().toLocaleTimeString()
                };
            } else {
                parsedMessage = {
                    type: 'Unknown',
                    hwid: 'Unknown',
                    data: rawMessage,
                    timestamp: new Date().toLocaleTimeString()
                };
            }
            setMessages((prev) => [...prev, parsedMessage].slice(-100));
        };
        socket.onclose = () => {
            setMessages(prev => [...prev, 'Disconnected from WebSocket']);
        };
        return () => {
            socket.close();
            setDeviceHwMode(device.hwid, 1);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device.hwid, device.type]);

    useEffect(() => {
        const scrollInterval = setInterval(() => {
            const logContainer = document.getElementById('v4-debug-log-container');
            if (logContainer && autoScroll) {
                logContainer.scrollTop = logContainer.scrollHeight;
            }
        }, 100);
        return () => clearInterval(scrollInterval);
    }, [autoScroll]);

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', width: '80%', maxWidth: '900px', height: '80%', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0 }}>Serial Stream: {device.hwid}</h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <label><input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} /> Auto Scroll</label>
                        <Button variant="danger" onClick={() => setDeviceHwMode(device.hwid, -1)}>Reset</Button>
                        <Button variant="secondary" onClick={onClose}>Close</Button>
                    </div>
                </div>
                <div id="v4-debug-log-container" style={{ backgroundColor: '#1e1e1e', color: '#00ff00', padding: '10px', flex: 1, overflowY: 'auto', borderRadius: '5px', fontFamily: 'monospace' }}>
                    {messages.map((m, i) => (
                        <div key={i} style={{ marginBottom: '5px', borderBottom: '1px solid #333' }}>
                            {typeof m === 'string' ? `> ${m}` : (
                                <>
                                    <span style={{ color: '#888' }}>[{m.timestamp}]</span>
                                    <strong style={{ color: '#007bff' }}> {m.type}:{m.hwid}</strong>
                                    <span> {m.data}</span>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Device Token provisioning (V4 per-device auth) ---
// Gen a per-device token for an hwid, show the plaintext ONCE (copy into firmware before
// flashing), list existing tokens, and revoke. Backend gates every call with
// hwcontrol:provision, so operators without it get 403 here.
const DeviceTokens = () => {
    const API_BASE = `${API_BASE_URL}/hwcontrol/api/v3/hardware/tokens`;
    const GROUPS_API = `${API_BASE_URL}/hwcontrol/api/v3/hardware/token-groups`;
    const [tokens, setTokens] = useState([]);
    const [tagGroups, setTagGroups] = useState([]);
    const [hwid, setHwid] = useState('');
    const [label, setLabel] = useState('');
    const [tagGroupId, setTagGroupId] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const [plaintext, setPlaintext] = useState(null); // shown once after gen
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tokenSubTab, setTokenSubTab] = useState('Active');
    const [viewOpenId, setViewOpenId] = useState(null);
    const [viewData, setViewData] = useState({}); // id -> { loading, token, error, gone }

    const authHeader = () => {
        const token = JSON.parse(localStorage.getItem("user"))?.token;
        return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    };

    const load = async () => {
        setError(null);
        try {
            const resp = await fetch(API_BASE, { headers: authHeader() });
            if (resp.status === 403) { setError('คุณไม่มีสิทธิ์ hwcontrol:provision'); setTokens([]); return; }
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            setTokens(await resp.json());
        } catch (e) { setError(`โหลดรายการไม่สำเร็จ: ${e.message}`); }
    };

    const loadGroups = async () => {
        try {
            const resp = await fetch(GROUPS_API, { headers: authHeader() });
            if (resp.status === 403) return;
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            setTagGroups(await resp.json());
        } catch (e) { setError(`โหลด Tag Group ไม่สำเร็จ: ${e.message}`); }
    };

    useEffect(() => { load(); loadGroups(); /* eslint-disable-next-line */ }, []);

    const createGroup = async () => {
        const name = newGroupName.trim();
        if (!name) { setError('กรุณาระบุชื่อ Tag Group'); return; }
        setError(null);
        try {
            const resp = await fetch(GROUPS_API, {
                method: 'POST', headers: authHeader(), body: JSON.stringify({ name })
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const group = await resp.json();
            setTagGroups(prev => [...prev, group].sort((a, b) => a.name.localeCompare(b.name)));
            setTagGroupId(String(group.id));
            setNewGroupName('');
        } catch (e) { setError(`สร้าง Tag Group ไม่สำเร็จ: ${e.message}`); }
    };

    const gen = async () => {
        setError(null); setPlaintext(null);
        if (!hwid.trim()) { setError('กรุณาระบุ hwid'); return; }
        setLoading(true);
        try {
            const resp = await fetch(API_BASE, {
                method: 'POST', headers: authHeader(),
                body: JSON.stringify({
                    hwid: hwid.trim(),
                    label: label.trim(),
                    tagGroupId: tagGroupId ? Number(tagGroupId) : null
                })
            });
            if (resp.status === 403) { setError('คุณไม่มีสิทธิ์ hwcontrol:provision'); return; }
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            setPlaintext(data); // { id, hwid, label, token, createdAt }
            setHwid(''); setLabel(''); setTagGroupId('');
            load();
        } catch (e) { setError(`สร้าง token ไม่สำเร็จ: ${e.message}`); }
        finally { setLoading(false); }
    };

    const revoke = async (id) => {
        if (!window.confirm('ยืนยัน revoke token นี้? อุปกรณ์จะใช้ token นี้ไม่ได้ทันที')) return;
        setError(null);
        try {
            const resp = await fetch(`${API_BASE}/${id}/revoke`, { method: 'POST', headers: authHeader() });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            load();
        } catch (e) { setError(`revoke ไม่สำเร็จ: ${e.message}`); }
    };

    const fetchView = async (id) => {
        setViewData(prev => ({ ...prev, [id]: { loading: true } }));
        try {
            const resp = await fetch(`${API_BASE}/${id}/view`, { headers: authHeader() });
            if (resp.status === 410) {
                setViewData(prev => ({ ...prev, [id]: { loading: false, gone: true } }));
                return;
            }
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            setViewData(prev => ({ ...prev, [id]: { loading: false, token: data.token } }));
        } catch (e) {
            setViewData(prev => ({ ...prev, [id]: { loading: false, error: e.message } }));
        }
    };

    const toggleView = (id) => {
        if (viewOpenId === id) { setViewOpenId(null); return; }
        setViewOpenId(id);
        if (!viewData[id]) fetchView(id);
    };

    const filteredTokens = tokens.filter(t => tokenSubTab === 'Active' ? !t.revoked : t.revoked);

    return (
        <div style={{ maxWidth: '900px' }}>
            <h3>Device Tokens (per-device auth)</h3>
            <p style={{ color: '#666', fontSize: '13px' }}>
                สร้าง token ประจำเครื่อง (1 hwid = 1 identity) — plaintext จะแสดง <b>ครั้งเดียว</b> ตอนสร้าง
                ให้ copy ไปฝัง firmware ก่อน flash. ต้องมีสิทธิ์ <code>hwcontrol:provision</code>.
            </p>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
                <input placeholder="hwid" value={hwid} onChange={e => setHwid(e.target.value)}
                    style={{ padding: '8px', flex: '0 0 220px' }} />
                <input placeholder="label (เช่น ชื่อ/ตำแหน่งเครื่อง)" value={label} onChange={e => setLabel(e.target.value)}
                    style={{ padding: '8px', flex: 1 }} />
                <select value={tagGroupId} onChange={e => setTagGroupId(e.target.value)}
                    style={{ padding: '8px', minWidth: '160px' }}>
                    <option value="">ไม่จัดกลุ่ม</option>
                    {tagGroups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
                </select>
                <button onClick={gen} disabled={loading}
                    style={{ padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    {loading ? '...' : 'Gen Token'}
                </button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
                <input placeholder="ชื่อ Tag Group ใหม่" value={newGroupName}
                    onChange={e => setNewGroupName(e.target.value)} style={{ padding: '8px', flex: '0 0 280px' }} />
                <button onClick={createGroup} style={{ padding: '8px 14px', border: '1px solid #007bff', borderRadius: '4px', background: 'white', color: '#007bff', cursor: 'pointer' }}>
                    + สร้าง Tag Group
                </button>
            </div>

            {error && <div style={{ color: '#c00', marginBottom: '10px' }}>⚠️ {error}</div>}

            {plaintext && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffd54f', padding: '12px', borderRadius: '4px', marginBottom: '12px' }}>
                    <b>✅ สร้างสำเร็จ — copy token นี้เดี๋ยวนี้ (จะไม่แสดงอีก):</b>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                        <code style={{ flex: 1, wordBreak: 'break-all', background: '#fff', padding: '8px', border: '1px solid #ddd' }}>
                            {plaintext.token}
                        </code>
                        <button onClick={() => navigator.clipboard.writeText(plaintext.token)}
                            style={{ padding: '8px 12px', cursor: 'pointer' }}>Copy</button>
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>hwid: {plaintext.hwid}</div>
                </div>
            )}

            <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '10px' }}>
                {['Active', 'Revoked'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setTokenSubTab(tab)}
                        style={{
                            padding: '6px 14px',
                            cursor: 'pointer',
                            backgroundColor: tokenSubTab === tab ? '#007bff' : '#f8f9fa',
                            color: tokenSubTab === tab ? 'white' : 'black',
                            border: 'none',
                            borderBottom: tokenSubTab === tab ? '2px solid #0056b3' : 'none',
                            marginRight: '5px',
                            borderRadius: '4px 4px 0 0',
                            fontWeight: 'bold',
                            fontSize: '13px'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
                        <th style={{ padding: '8px' }}>id</th>
                        <th style={{ padding: '8px' }}>hwid</th>
                        <th style={{ padding: '8px' }}>label</th>
                        <th style={{ padding: '8px' }}>Tag Group</th>
                        <th style={{ padding: '8px' }}>created</th>
                        <th style={{ padding: '8px' }}>status</th>
                        <th style={{ padding: '8px' }}></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredTokens.map(t => (
                        <React.Fragment key={t.id}>
                            <tr style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '8px' }}>{t.id}</td>
                                <td style={{ padding: '8px' }}>{t.hwid}</td>
                                <td style={{ padding: '8px' }}>{t.label}</td>
                                <td style={{ padding: '8px' }}>{t.tagGroupName || '-'}</td>
                                <td style={{ padding: '8px' }}>{t.createdAt ? new Date(t.createdAt).toLocaleString() : ''}</td>
                                <td style={{ padding: '8px' }}>{t.revoked ? '🚫 revoked' : '✅ active'}</td>
                                <td style={{ padding: '8px' }}>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        {tokenSubTab === 'Active' && (
                                            <button onClick={() => toggleView(t.id)}
                                                style={{ padding: '4px 10px', background: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                {viewOpenId === t.id ? 'Hide' : 'View'}
                                            </button>
                                        )}
                                        {!t.revoked && (
                                            <button onClick={() => revoke(t.id)}
                                                style={{ padding: '4px 10px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                Revoke
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                            {viewOpenId === t.id && (
                                <tr style={{ borderBottom: '1px solid #eee', background: '#f8f9fa' }}>
                                    <td colSpan="7" style={{ padding: '8px 8px 8px 24px' }}>
                                        {viewData[t.id]?.loading && <span style={{ color: '#666' }}>กำลังโหลด...</span>}
                                        {viewData[t.id]?.gone && (
                                            <span style={{ color: '#c00' }}>ไม่สามารถดู token นี้ได้ (สร้างก่อนรองรับฟีเจอร์นี้)</span>
                                        )}
                                        {viewData[t.id]?.error && (
                                            <span style={{ color: '#c00' }}>โหลด token ไม่สำเร็จ: {viewData[t.id].error}</span>
                                        )}
                                        {viewData[t.id]?.token && (
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <code style={{ flex: 1, wordBreak: 'break-all', background: '#fff', padding: '8px', border: '1px solid #ddd' }}>
                                                    {viewData[t.id].token}
                                                </code>
                                                <button onClick={() => navigator.clipboard.writeText(viewData[t.id].token)}
                                                    style={{ padding: '6px 12px', cursor: 'pointer' }}>Copy</button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                    {filteredTokens.length === 0 && !error && (
                        <tr><td colSpan="7" style={{ padding: '12px', color: '#999' }}>
                            {tokenSubTab === 'Active' ? 'ยังไม่มี token ที่ active' : 'ยังไม่มี token ที่ถูก revoke'}
                        </td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default HardWare;
